import * as BaseFilePond from 'filepond';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FilePond } from 'react-filepond';
import { PsFileUploadHandler } from '../handlers';
import { FileItem } from '../types';
import usePsContext from './ps-context';
import usePsFetch from './ps-fetch';
import fileDownloadPlugin from './ps-file-upload-download-plugin';
import {
  createPsFileUploadPlaceholder,
  PS_FILE_UPLOAD_MAX_FILE_SIZE,
  PS_FILE_UPLOAD_MAX_TOTAL_FILE_SIZE
} from './ps-file-upload.utils';

// BaseFilePond.registerPlugin(FilePondPluginFileValidateType);
//todo: has a bug that displays ID than filename
BaseFilePond.registerPlugin(fileDownloadPlugin);
// BaseFilePond.registerPlugin(FilePondPluginFileValidateSize);
// BaseFilePond.registerPlugin(FilePondPluginImageValidateSize);

export interface Props {
  url?: string;
  initFiles?: FileItem[];
  /**
   * Opt-in: show `initFiles` as placeholders (no download on init) and fetch
   * each one's content through this loader only when needed. `url` is then
   * not used for loading.
   */
  loadInitFile?: (file: FileItem) => Promise<Blob>;
  allowMultiple?: boolean;
  disabled?: boolean;
  onAddFile?: (file: BaseFilePond.FilePondFile) => void;

  validateFileType?: boolean;
  acceptedFileTypes?: string[];

  disableFileSizeValidation?: boolean;
  maxFileSize?: number;
  maxTotalFileSize?: number;

  validateImageSize?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  contextOverride?: { api: string; path: string };
}

const PsFileUpload = React.forwardRef<PsFileUploadHandler | null, Props>(
  (
    {
      url,
      initFiles,
      loadInitFile,
      allowMultiple = true,
      disabled = false,
      onAddFile,
      validateFileType = false,
      acceptedFileTypes,
      disableFileSizeValidation = false,
      maxFileSize = PS_FILE_UPLOAD_MAX_FILE_SIZE,
      maxTotalFileSize = PS_FILE_UPLOAD_MAX_TOTAL_FILE_SIZE,
      validateImageSize = false,
      imageWidth = 100,
      imageHeight = 100,
      contextOverride
    },
    ref: React.Ref<PsFileUploadHandler>
  ) => {
    const [files, setFiles] = useState<any[]>([]);
    const pond = useRef<FilePond>(null);
    const fetch = usePsFetch();
    const context = contextOverride ?? usePsContext();

    useEffect(() => {
      if (files.length === 0) {
        init(initFiles ?? []);
      }
    }, [initFiles]);

    const handleUploadFiles = (files: any) => {
      setFiles(files);
    };

    useImperativeHandle(ref, () => {
      return {
        // Until FilePond syncs back via onupdatefiles, `files` still holds the
        // init descriptors; expose their placeholder (if any) instead.
        files: files.map((f) => f?.options?.file ?? f),
        reset: () => setFiles([]),
        init: init,
        hasValidFileSize: hasValidFileSize()
      };
    });

    const init = (fileItems: FileItem[]) => {
      if (!!fileItems) {
        const files: any[] = fileItems.map((f) =>
          loadInitFile
            ? {
                source: '' + f.id,
                // A mock `file` makes FilePond skip `server.load`.
                options: {
                  type: 'local',
                  file: createPsFileUploadPlaceholder(f, loadInitFile)
                }
              }
            : {
                filename: f.name,
                source: '' + f.id,
                options: { type: 'local' }
              }
        );
        setFiles(files);
      }
    };

    const hasValidFileSize = (): boolean => {
      if (!!files) {
        let totalSize = 0;
        for (const f of files) {
          totalSize += f.size;
          if (f.size > maxFileSize) return false;
        }
        if (totalSize > maxTotalFileSize) return false;
      }
      return true;
    };

    const server = () => {
      return !!url
        ? {
            url: context.api + context.path,
            timeout: 7000,
            load: {
              url,
              method: 'GET' as const,
              headers: fetch.request('GET' as const).headers as Record<
                string,
                string
              >,
              withCredentials: false
            },
            revert: null,
            restore: null,
            process: null,
            fetch: null
          }
        : null;
    };

    const isRemoveDisabled = () => {
      return !disabled;
    };
    return (
      <FilePond
        ref={pond}
        allowMultiple={allowMultiple}
        labelIdle={
          disabled
            ? ''
            : 'Drag & Drop your ' +
              (allowMultiple ? 'files' : 'file') +
              ' or <span class="filepond--label-action"> Browse </span>'
        }
        files={files}
        server={server()}
        onaddfile={(_e, file) => void onAddFile?.(file)}
        allowDrop={!disabled}
        allowBrowse={!disabled}
        allowPaste={!disabled}
        beforeRemoveFile={isRemoveDisabled}
        onupdatefiles={(fileItems) =>
          handleUploadFiles(fileItems.map((f) => f.file))
        }
        allowFileTypeValidation={validateFileType}
        acceptedFileTypes={acceptedFileTypes}
        allowFileSizeValidation={!disableFileSizeValidation}
        maxFileSize={maxFileSize + 'B'}
        maxTotalFileSize={maxTotalFileSize + 'B'}
        allowImageValidateSize={validateImageSize}
        imageValidateSizeMinWidth={imageWidth}
        imageValidateSizeMaxWidth={imageWidth}
        imageValidateSizeMinHeight={imageHeight}
        imageValidateSizeMaxHeight={imageHeight}
      />
    );
  }
);

export default PsFileUpload;
